<script>
import { defineComponent } from 'vue'
import MlbbTeamResultBlock from './MlbbTeamResultBlock.vue'

export default defineComponent({
  name: 'MlbbMatchStats',
  components: { MlbbTeamResultBlock },
  props: {
    matchData: {
      type: Object,
      default: null
    },
    teams: {
      type: Array,
      default: () => []
    },
    gameNumber: {
      type: Number,
      default: null
    }
  },
  computed: {
    hostGroupTeamId() {
      return this.matchData?.host_group_team_id ?? null
    },
    guestGroupTeamId() {
      return this.matchData?.guest_group_team_id ?? null
    },
    winnerGroupTeamId() {
      return this.matchData?.winner_group_team_id ?? null
    },
    resultPlayers() {
      return this.matchData?.result_players ?? []
    },
    participants() {
      return this.matchData?.participants ?? []
    },
    hostTeamName() {
      return this.teamNameByGroupId(this.hostGroupTeamId)
    },
    guestTeamName() {
      return this.teamNameByGroupId(this.guestGroupTeamId)
    },
    hostResultPlayers() {
      return this.resultPlayers.filter(p => p?.group_team_id === this.hostGroupTeamId)
    },
    guestResultPlayers() {
      return this.resultPlayers.filter(p => p?.group_team_id === this.guestGroupTeamId)
    },
    hostMembers() {
      return this.participants.filter(p => p?.group_team_id === this.hostGroupTeamId)
    },
    guestMembers() {
      return this.participants.filter(p => p?.group_team_id === this.guestGroupTeamId)
    },
    hostRows() {
      return this.buildTeamRows(this.hostMembers, this.hostResultPlayers)
    },
    guestRows() {
      return this.buildTeamRows(this.guestMembers, this.guestResultPlayers)
    },
    hostScore() {
      return this.sumKills(this.hostResultPlayers)
    },
    guestScore() {
      return this.sumKills(this.guestResultPlayers)
    },
    isHostWinner() {
      return this.winnerGroupTeamId != null && this.winnerGroupTeamId === this.hostGroupTeamId
    },
    isGuestWinner() {
      return this.winnerGroupTeamId != null && this.winnerGroupTeamId === this.guestGroupTeamId
    },
    parsedDate() {
      const raw = this.matchData?.check_in_deadline_at
      if (!raw) return null
      const d = new Date(raw)
      return isNaN(d.getTime()) ? null : d
    },
    matchTitle() {
      const d = this.parsedDate
      const gameSuffix = this.gameNumber != null ? ` (Игра ${this.gameNumber})` : ''
      if (d) {
        const pad = (n) => String(n).padStart(2, '0')
        return `MLBB Game ${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}${gameSuffix}`
      }
      return this.matchData?.id != null ? `MLBB Game #${this.matchData.id}${gameSuffix}` : `MLBB Game${gameSuffix}`
    },
    formattedDate() {
      const d = this.parsedDate
      if (!d) return ''
      const pad = (n) => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
    }
  },
  methods: {
    teamNameByGroupId(groupTeamId) {
      if (groupTeamId == null) return ''
      const team = (this.teams || []).find(t => t.group_team_id === groupTeamId)
      return team?.name || ''
    },
    sumKills(arr) {
      return arr.reduce((acc, p) => acc + (Number(p?.kills) || 0), 0)
    },
    formatGold(value) {
      if (value == null) return '0'
      try {
        return Number(value).toLocaleString('ru-RU')
      } catch (e) {
        return String(value)
      }
    },
    buildTeamRows(members, players) {
      const used = new Set()

      const memberRows = (members || []).map((m, idx) => {
        const nick = m?.name
        let matchedIdx = -1
        if (nick) {
          matchedIdx = players.findIndex(
            (p, i) => !used.has(i) && p?.in_game_name && p.in_game_name === nick
          )
        }
        if (matchedIdx === -1 && m?.user_id != null) {
          matchedIdx = players.findIndex(
            (p, i) => !used.has(i) && p?.user_id === m.user_id
          )
        }
        const stats = matchedIdx >= 0 ? players[matchedIdx] : null
        if (matchedIdx >= 0) used.add(matchedIdx)

        return {
          key: `m-${m?.user_id ?? idx}`,
          nick: nick || stats?.in_game_name || '—',
          countryCode: m?.country_code || null,
          countryName: m?.country_name || null,
          avatar: stats?.hero?.avatar || null,
          heroName: stats?.hero?.name,
          level: stats?.level,
          kills: stats?.kills,
          deaths: stats?.deaths,
          assists: stats?.assists,
          gold: stats?.gold,
          isMvp: !!stats?.is_mvp,
          played: !!stats
        }
      })

      const orphans = players
        .map((p, i) => ({ p, i }))
        .filter(({ i }) => !used.has(i))
        .map(({ p, i }) => ({
          key: `p-${p?.id ?? i}`,
          nick: p?.in_game_name || '—',
          countryCode: null,
          countryName: null,
          avatar: p?.hero?.avatar || null,
          heroName: p?.hero?.name,
          level: p?.level,
          kills: p?.kills,
          deaths: p?.deaths,
          assists: p?.assists,
          gold: p?.gold,
          isMvp: !!p?.is_mvp,
          played: true
        }))

      return [...memberRows, ...orphans]
    }
  }
})
</script>

<template>
  <div class="mlbb-match-stats">
    <p class="section-title">Детальная статистика матча</p>

    <div class="result-header">
      <div class="header-side-left">
        <p class="game-title">{{ matchTitle }}</p>
        <p v-if="formattedDate" class="game-date">{{ formattedDate }}</p>
      </div>

      <div class="header-mid">
        <p class="team-name team-host">{{ hostTeamName }}</p>
        <p v-if="isHostWinner" class="victory-label">VICTORY</p>
      </div>

      <div class="header-score">
        <p class="score-number">{{ hostScore }}</p>
        <p class="score-number">{{ guestScore }}</p>
      </div>

      <div class="header-mid header-mid-right">
        <p class="team-name team-guest">{{ guestTeamName }}</p>
        <p v-if="isGuestWinner" class="victory-label">VICTORY</p>
      </div>
    </div>

    <div class="teams-result-grid">
      <MlbbTeamResultBlock
        :name="hostTeamName"
        :rows="hostRows"
        :is-winner="isHostWinner"
        color="red"
        :format-gold="formatGold"
      />
      <MlbbTeamResultBlock
        :name="guestTeamName"
        :rows="guestRows"
        :is-winner="isGuestWinner"
        color="blue"
        :format-gold="formatGold"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.mlbb-match-stats {
  margin-top: 24px;

  .section-title {
    text-align: center;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 2px;
    color: #6b6f7c;
    text-transform: uppercase;
    margin-bottom: 14px;
  }

  .result-header {
    background: linear-gradient(180deg, #1b2230 0%, #11151f 100%);
    color: #fff;
    border-radius: 14px;
    padding: 18px 20px;
    display: grid;
    grid-template-columns: 1.4fr 1fr auto 1fr;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.08);

    @media (max-width: 700px) {
      grid-template-columns: 1fr;
      text-align: center;
    }

    .header-side-left {
      .game-title {
        margin: 0;
        font-weight: 700;
        font-size: 18px;
        color: #fff;
      }

      .game-date {
        margin: 4px 0 0;
        font-size: 12px;
        color: #8d93a3;
      }
    }

    .header-mid {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 4px;
      min-width: 0;

      &.header-mid-right {
        align-items: flex-end;
      }

      .team-name {
        margin: 0;
        font-weight: 700;
        font-size: 18px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;

        &.team-host {
          color: #ff5466;
        }

        &.team-guest {
          color: #5aa1ff;
        }
      }

      .victory-label {
        margin: 0;
        font-size: 10px;
        font-weight: 800;
        letter-spacing: 3px;
        color: #f6c453;
      }
    }

    .header-score {
      display: flex;
      align-items: center;
      gap: 14px;

      .score-number {
        margin: 0;
        font-size: 38px;
        font-weight: 800;
        line-height: 1;
        color: #fff;
      }
    }
  }

  .teams-result-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
    gap: 16px;
  }
}
</style>
